import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";
import { useDestinos, useSalvarDestino } from "@/hooks/use-cargas";
import { useToast } from "@/hooks/use-toast";

/** O valor do item "criar novo" — não é id de destino nenhum. */
const NOVO = "__novo__";

/**
 * "Para quem eu vendo" — o seletor de destino, com criação em linha.
 *
 * ⚠️ Criar sem sair da tela é o ponto: o destino aparece pela primeira vez no
 * meio de montar a carga ("vou mandar pro Atacadão"), e mandar o comprador para
 * um cadastro em outra rota para voltar depois é como a carga fica sem destino.
 * É o mesmo desenho do `DialogComprador` do lado do produtor.
 *
 * `kgCaixa` é do DESTINO e não da carga: cada distribuidor conta em caixa de um
 * peso, e é o peso dele que sai no PDF (PR B) para a conferência dele fechar sem
 * conta de cabeça.
 */
export function SelectDestino({
  valor,
  onChange,
  desabilitado,
}: {
  valor: string | null;
  onChange: (id: string | null) => void;
  desabilitado?: boolean;
}) {
  const { toast } = useToast();
  const { data: destinos } = useDestinos(true);
  const salvar = useSalvarDestino();
  const [criando, setCriando] = useState(false);
  const [nome, setNome] = useState("");
  const [cidade, setCidade] = useState("");
  const [telefone, setTelefone] = useState("");
  const [kgCaixa, setKgCaixa] = useState("");

  function criar() {
    if (!nome.trim()) return;
    salvar.mutate(
      {
        nome: nome.trim(),
        cidade: cidade.trim() || null,
        telefone: telefone.trim() || null,
        kgCaixa: kgCaixa ? parseInt(kgCaixa, 10) : null,
      },
      {
        onSuccess: (destino) => {
          onChange(destino.id);
          setCriando(false);
          setNome(""); setCidade(""); setTelefone(""); setKgCaixa("");
        },
        onError: (e: Error) => toast({
          title: "Não consegui salvar o destino",
          description: e.message,
          variant: "destructive",
        }),
      },
    );
  }

  if (criando) {
    return (
      <div className="space-y-3 rounded-lg border p-3" data-testid="form-novo-destino">
        <div className="space-y-2">
          <Label htmlFor="destino-nome">Nome do destino</Label>
          <Input
            id="destino-nome" value={nome} onChange={e => setNome(e.target.value)}
            placeholder="Atacadão Frutas" autoFocus data-testid="input-destino-nome"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <Label htmlFor="destino-cidade">Cidade</Label>
            <Input id="destino-cidade" value={cidade} onChange={e => setCidade(e.target.value)} placeholder="Feira de Santana" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="destino-kgcaixa">kg por caixa</Label>
            <Input
              id="destino-kgcaixa" type="number" inputMode="numeric"
              value={kgCaixa} onChange={e => setKgCaixa(e.target.value)} placeholder="22"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="destino-telefone">WhatsApp</Label>
          <Input
            id="destino-telefone" inputMode="tel" value={telefone}
            onChange={e => setTelefone(e.target.value)} placeholder="(74) 99999-8888"
          />
        </div>
        <div className="flex gap-2">
          <Button type="button" variant="outline" className="flex-1" onClick={() => setCriando(false)}>Cancelar</Button>
          <Button type="button" className="flex-1" onClick={criar} disabled={!nome.trim() || salvar.isPending}>
            {salvar.isPending ? "Salvando…" : "Salvar destino"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="select-destino">Destino</Label>
      <Select
        value={valor ?? ""}
        disabled={desabilitado}
        onValueChange={v => (v === NOVO ? setCriando(true) : onChange(v || null))}
      >
        <SelectTrigger id="select-destino" data-testid="select-destino">
          <SelectValue placeholder="Escolher destino" />
        </SelectTrigger>
        <SelectContent>
          {(destinos ?? []).map(d => (
            <SelectItem key={d.id} value={d.id}>
              {d.nome}{d.cidade ? `, ${d.cidade}` : ""}
            </SelectItem>
          ))}
          <SelectItem value={NOVO}>
            <span className="flex items-center gap-1"><Plus className="h-3.5 w-3.5" />Novo destino</span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
